// Etiket Yazdırma sayfası kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'inventory.labelPrinting';

export const inventoryLabelPrintingGuide = (t: TFunction): GuideContent => ({
  // Sayfada şablon kartları; "Etiket Yazdır" penceresinde solda ürün seçimi, sağda şablon / yazıcı / önizleme
  layout: 'split',
  title: t(`${G}.title`, 'Etiket nasıl yazdırılır?'),
  intro: t(`${G}.intro`, 'Bu ekranda etiket şablonlarınızı hazırlar, ürünleri seçip toplu etiket basarsınız. Harita, "Etiket Yazdır" penceresini gösterir; bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Şablonlar ve Etiket Yazdır'),
    list: t(`${G}.regions.list`, 'Ürün seçimi ve adet'),
    form: t(`${G}.regions.form`, 'Şablon, yazıcı ve önizleme'),
    footer: t(`${G}.regions.footer`, 'Yazdır / PDF İndir / Yazıcıya Bas'),
  },
  steps: [
    {
      region: 'header',
      title: t(`${G}.s1.title`, '1. Etiket şablonunu hazırlayın'),
      text: t(`${G}.s1.text`, '"Yeni Şablon" ile hazır bir düzen seçin (Raf Fiyat Etiketi, Ürün Barkod Etiketi, Tartı Etiketi, A4 Sayfa Etiketi, Depo Lokasyon Etiketi veya Boş Şablon). Tasarımcı açılır; ölçüleri ve içeriği değiştirip kaydedin. Kayıtlı şablonlar sayfada kart olarak listelenir.'),
      tips: [
        t(`${G}.s1.tip1`, 'Karttaki "Düzenle" tasarımcıyı açar, "Bas" o şablonla yazdırma penceresini açar.'),
        t(`${G}.s1.tip2`, 'Yıldız simgesi şablonu varsayılan yapar; yazdırma penceresi açılınca önce varsayılan şablon seçili gelir.'),
        t(`${G}.s1.tip3`, 'Kartın ⋮ menüsünde Kopyala, Dışa Aktar ve Sil bulunur. Dışa aktarılan dosyayı başka yerde "İçe Aktar" ile yükleyebilirsiniz.'),
        t(`${G}.s1.tip4`, 'Tasarımcıdan yazdırırken şablon önce kaydedilir; böylece ekranda gördüğünüz ile basılan aynı olur.'),
      ],
    },
    {
      region: 'list',
      title: t(`${G}.s2.title`, '2. Basılacak ürünleri seçin'),
      text: t(`${G}.s2.text`, '"Etiket Yazdır" ile pencereyi açın. Sol üstteki kutuya ürün adı, stok kodu veya barkod yazın; barkod okutup Enter\'a basarsanız ürün doğrudan listeye eklenir. Satırdaki + düğmesi tek ürünü ekler.'),
      tips: [
        t(`${G}.s2.tip1`, 'Kutucuklarla birkaç ürün işaretleyip "Seçilenleri ekle" deyin. "Tüm sonuçları ekle" aramaya uyan ürünlerin hepsini (en fazla 500) ekler; listede zaten olanlar atlanır.'),
        t(`${G}.s2.tip2`, '"Fiyat değişimi" Başlangıç / Bitiş tarihleri, bu aralıkta fiyatı değişmiş ürünleri listeler. Fiyatı değişen ürünlerin raf etiketini yenilemek için kullanışlıdır.'),
      ],
    },
    {
      region: 'list',
      title: t(`${G}.s3.title`, '3. Etiket adedini girin'),
      text: t(`${G}.s3.text`, 'Seçilen ürünler "Basılacak Etiketler" tablosunda görünür. Her ürün için "Adet" alanına kaç etiket basılacağını yazın; gerekmeyen satırı kaldırın. Üstteki etikette toplam ürün ve etiket sayısı yazar.'),
      tips: [
        t(`${G}.s3.tip1`, 'Listede olan bir ürünü tekrar eklerseniz yeni satır açılmaz, adedi artar.'),
      ],
    },
    {
      region: 'form',
      title: t(`${G}.s4.title`, '4. Şablonu seçin, önizlemeyi kontrol edin'),
      text: t(`${G}.s4.text`, 'Sağdaki "Etiket Şablonu" listesinden şablonu seçin. Altta toplam etiket ve sayfa sayısı görünür; "Önizleme" alanında okla sayfalar arasında gezinerek sonucu kontrol edin.'),
      tips: [
        t(`${G}.s4.tip1`, 'A4 gibi sayfa şablonlarında "Baştan atlanacak göz" ile yarım kullanılmış bir etiket sayfasının dolu gözlerini atlayabilirsiniz.'),
        t(`${G}.s4.tip2`, 'Barkodu olmayan ürün varsa uyarı çıkar; o etiketlerde barkod alanı boş basılır.'),
      ],
    },
    {
      region: 'form',
      title: t(`${G}.s5.title`, '5. Etiket yazıcısını seçin'),
      text: t(`${G}.s5.text`, '"Doğrudan yazıcıya bas" bölümünde "Etiket Yazıcısı" listesinden yazıcınızı seçin: USB yazıcılar, bilgisayarda kurulu yazıcılar ve ağ yazıcıları burada görünür. Listede yoksa "Yenile" deyin ya da "Elle hedef gir…" ile ağ adresini veya Windows yazıcı adını yazın. Seçiminiz bir sonraki açılışta hatırlanır.'),
      tips: [
        t(`${G}.s5.tip1`, '"Yazıcı Dili" yazıcınızın dili olmalıdır. Yazıcıyı FEED tuşu basılı tutarak açınca çıkan yapılandırma etiketinde dili görebilirsiniz. Boş etiket çıkıyorsa dil yanlış seçilmiştir.'),
        t(`${G}.s5.tip2`, 'Yazıcı bulunamazsa yazıcının açık ve bağlı olduğundan emin olun.'),
      ],
    },
    {
      region: 'footer',
      title: t(`${G}.s6.title`, '6. Yazdırın'),
      text: t(`${G}.s6.text`, '"Yazdır" bilgisayarın yazdırma penceresini açar. "PDF İndir" sayfaları PDF dosyası olarak kaydeder. "Yazıcıya Bas" ise yazdırma penceresi açmadan seçili etiket yazıcısına doğrudan gönderir; bunun için yazıcı seçilmiş ve yazıcı dili destekleniyor olmalıdır.'),
      tips: [
        t(`${G}.s6.tip1`, 'Yazdırma penceresinde hedef olarak "PDF olarak kaydet" seçerseniz metin keskin kalır.'),
        t(`${G}.s6.tip2`, 'Başlıktaki (i) düğmesi bu rehberi her zaman yeniden açar.'),
      ],
    },
  ],
});
